import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Grid
} from '@mui/material';
import {
  Search,
  TrendingUp,
  TrendingDown,
  BarChart,
  Speed,
  ShowChart
} from '@mui/icons-material';

const exampleCategories = {
  trend: {
    icon: <TrendingUp />,
    label: 'Trend Following',
    color: 'success',
    examples: [
      { query: '1D CLOSE > 1D SMA(20)', desc: 'Above 20-day MA' },
      { query: '1D SMA(50) > 1D SMA(200)', desc: 'Golden Cross Setup' },
      { query: '1D CLOSE > 1D SUPERTREND(7,3)', desc: 'Supertrend Bullish' },
      { query: '1D CLOSE > 1D EMA(21)', desc: 'Above 21-day EMA' },
      { query: '1D TEMA(21) > 1D TEMA(21)[1]', desc: 'Triple EMA Rising' },
      { query: '1D CLOSE > 1D VWAP', desc: 'Above VWAP' },
      { query: '1D CLOSE > 1D TEMA(50) AND 1D TEMA(50) > 1D TEMA(200)', desc: 'Strong Uptrend' }
    ]
  },
  bullish: {
    icon: <TrendingUp />,
    label: 'Bullish Patterns',
    color: 'success',
    examples: [
      { query: '1D CLOSE[1] < 1D OPEN[1] AND 1D CLOSE > 1D OPEN AND 1D CLOSE > 1D HIGH[1]', desc: 'Bullish Engulfing' },
      { query: '1D LOW < 1D SMA(20) AND 1D CLOSE > 1D SMA(20)', desc: 'Support Bounce' },
      { query: '1D CLOSE > 1D HIGH[1] AND 1D VOLUME > 1D SMA(VOLUME,20)', desc: 'High Volume Breakout' },
      { query: '1D CLOSE > 1D BBANDS_UPPER(20,2)', desc: 'Bollinger Band Breakout' },
      { query: '1D RSI(14) < 30 AND 1D CLOSE > 1D OPEN', desc: 'Oversold Reversal' }
    ]
  },
  bearish: {
    icon: <TrendingDown />,
    label: 'Bearish Patterns',
    color: 'error',
    examples: [
      { query: '1D CLOSE[1] > 1D OPEN[1] AND 1D CLOSE < 1D OPEN AND 1D CLOSE < 1D LOW[1]', desc: 'Bearish Engulfing' },
      { query: '1D HIGH > 1D SMA(20) AND 1D CLOSE < 1D SMA(20)', desc: 'Resistance Rejection' },
      { query: '1D CLOSE < 1D LOW[1] AND 1D VOLUME > 1D SMA(VOLUME,20)', desc: 'High Volume Breakdown' },
      { query: '1D CLOSE < 1D BBANDS_LOWER(20,2)', desc: 'Below Lower Bollinger' },
      { query: '1D RSI(14) > 70 AND 1D CLOSE < 1D OPEN', desc: 'Overbought Reversal' }
    ]
  },
  momentum: {
    icon: <Speed />,
    label: 'Momentum',
    color: 'primary',
    examples: [
      { query: '1D RSI(14) > 70', desc: 'Overbought Territory' },
      { query: '1D RSI(14) < 30', desc: 'Oversold Territory' },
      { query: '1D MACD(12,26,9) > 0', desc: 'MACD Above Zero' },
      { query: '1D MACD(12,26,9) > 1D MACD_SIGNAL(12,26,9)', desc: 'MACD Crossover' },
      { query: '1D RSI(14) > 1D RSI(14)[1] AND 1D RSI(14)[1] > 1D RSI(14)[2]', desc: 'Rising RSI' },
      { query: '1D CLOSE > 1D OPEN AND 1D CLOSE[1] > 1D OPEN[1] AND 1D CLOSE[2] > 1D OPEN[2]', desc: '3 Green Candles' }
    ]
  },
  volume: {
    icon: <BarChart />,
    label: 'Volume Analysis',
    color: 'info',
    examples: [
      { query: '1D VOLUME > 1D SMA(VOLUME,20)', desc: 'Above Average Volume' },
      { query: '1D OBV > 1D OBV[5]', desc: 'On Balance Volume Rising' },
      { query: '1D VOLUME > 2 * 1D SMA(VOLUME,20)', desc: '2x Normal Volume' },
      { query: '1D VOLUME > 1D VOLUME[1] AND 1D VOLUME[1] > 1D VOLUME[2]', desc: 'Increasing Volume' },
      { query: '1D CLOSE > 1D OPEN AND 1D VOLUME > 1.5 * 1D SMA(VOLUME,20)', desc: 'Bullish Volume' }
    ]
  },
  volatility: {
    icon: <ShowChart />,
    label: 'Volatility',
    color: 'warning',
    examples: [
      { query: '1D BBANDS_WIDTH(20,2) > 1D SMA(BBANDS_WIDTH(20,2),20)', desc: 'Expanding Volatility' },
      { query: '1D CLOSE > 1D BBANDS_UPPER(20,2)', desc: 'Above Upper Band' },
      { query: '1D CLOSE < 1D BBANDS_LOWER(20,2)', desc: 'Below Lower Band' },
      { query: '1D HIGH - 1D LOW > 2 * 1D ATR(14)', desc: 'High Range Day' },
      { query: '1D BBANDS_WIDTH(20,2) < 1D SMA(BBANDS_WIDTH(20,2),20)', desc: 'Contracting Volatility' }
    ]
  }
};

const ExamplesPanel = ({ onExampleClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredExamples = Object.entries(exampleCategories).reduce((acc, [key, category]) => {
    if (selectedCategory !== 'all' && selectedCategory !== key) return acc;
    
    const filtered = category.examples.filter(example => 
      example.query.toLowerCase().includes(searchTerm.toLowerCase()) ||
      example.desc.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (filtered.length > 0) {
      acc[key] = { ...category, examples: filtered };
    }
    
    return acc;
  }, {});

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 700 }}>
        Example Queries
      </Typography>
      
      <Stack spacing={2} sx={{ mb: 3 }}>
        <TextField
          placeholder="Search examples..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label="All"
            variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
            onClick={() => setSelectedCategory('all')}
            size="small"
          />
          {Object.entries(exampleCategories).map(([key, category]) => (
            <Chip
              key={key}
              label={category.label}
              variant={selectedCategory === key ? 'filled' : 'outlined'}
              color={category.color}
              onClick={() => setSelectedCategory(key)}
              size="small"
              icon={category.icon}
            />
          ))}
        </Stack>
      </Stack>

      <Stack spacing={2}>
        {Object.entries(filteredExamples).map(([key, category]) => (
          <Box key={key}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              {category.icon}
              <Typography variant="subtitle1" fontWeight={600}>
                {category.label}
              </Typography>
            </Stack>
            
            <Grid container spacing={1}>
              {category.examples.map((example, index) => (
                <Grid item xs={12} key={index}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { 
                        transform: 'translateX(4px)',
                        boxShadow: 2
                      }
                    }}
                    onClick={() => onExampleClick && onExampleClick(example.query)}
                  >
                    <CardContent sx={{ py: 1.5, px: 2 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          fontWeight: 500,
                          mb: 0.5,
                          color: 'primary.main'
                        }}
                      >
                        {example.query}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {example.desc}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default ExamplesPanel;